import React, { useEffect, useState } from "react"
import ReactApexChart from "react-apexcharts"
import { Calendar } from "primereact/calendar"
import { gql, useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import useRequireAuth from "../../hooks/use-require-auth"
import PPUtils from "../../utils/PPUtils"

const query = gql`
    query getPriceHistory(
        $productId: Long!
        $fromMillis: Long
        $toMillis: Long
        $shopIds: [Long]
        $columnLang: String
    ) {
        priceHistory(
            productId: $productId
            fromMillis: $fromMillis
            toMillis: $toMillis
            shopIds: $shopIds
            columnLang: $columnLang
        ) {
            priceData {
                price
                totalPrice
                totalPriceByTotalPriceProvider
                secondPrice
                secondTotalPrice
                currencyId
                secondCurrencyId
                crawlDate
                cheapestPriceProvider
                cheapestPriceProviderByTotalPrice
            }
            shop {
                id
                name
                lang
                colorHex
            }
        }
    }
`

let startDateOneWeekAgo = new Date()
startDateOneWeekAgo.setDate(startDateOneWeekAgo.getDate() - 2 * 7)
startDateOneWeekAgo.setHours(0, 0, 0)

let endDate = new Date()
endDate = prepareEndDate(endDate)

const initialState = [startDateOneWeekAgo, endDate]

function prepareEndDate(endDate) {
    endDate.setHours(23, 59, 59)
    return endDate
}

function PPDateRangePicker({ value, onChange }) {
    return (
        <Calendar
            selectionMode="range"
            value={value}
            dateFormat="dd.mm.yy"
            showIcon
            selectOtherMonths
            showButtonBar
            readOnlyInput
            appendTo={document.body}
            baseZIndex={2000}
            onChange={(e) => {
                const next = e.value
                if (!Array.isArray(next)) {
                    return
                }
                if (next[0] && next[1]) {
                    onChange([next[0], prepareEndDate(new Date(next[1]))])
                    return
                }
                onChange(next)
            }}
        />
    )
}

function PPPriceHistory(props) {
    const [t] = useTranslation()
    const auth = useRequireAuth()
    const currentAuth = PPUtils.getCurrentAuthFromAuth(auth)
    const [dateRange, setDateRange] = useState(initialState)
    const [variables, setVariables] = useState()

    function handleDateChange(nextRange) {
        setDateRange(nextRange)
    }

    function getVisiblePriceHistory() {
        if (currentAuth?.customer?.name === "LG") {
            return priceHistory.filter((price) => Number(price?.shop?.id) !== 0)
        }
        return priceHistory
    }

    function getChartPrice(pd) {
        if (localStorage.getItem("showTotalPrice") === "true") {
            return pd.secondTotalPrice !== -1
                ? pd.secondTotalPrice.toFixed(2)
                : pd.totalPriceByTotalPriceProvider > 0
                ? pd.totalPriceByTotalPriceProvider.toFixed(2)
                : pd.totalPrice.toFixed(2)
        } else {
            return pd.secondPrice !== -1 ? pd.secondPrice.toFixed(2) : pd.price.toFixed(2)
        }
    }

    function getChartMerchantName(pd) {
        if (localStorage.getItem("showTotalPrice") === "true") {
            return pd.cheapestPriceProviderByTotalPrice != "" ? pd.cheapestPriceProviderByTotalPrice : ""
        } else {
            return pd.cheapestPriceProvider != "" ? pd.cheapestPriceProvider : ""
        }
    }

    function prepareSeriesDataForAreaChart() {
        const visible = getVisiblePriceHistory()
        if (localStorage.getItem("switchCurrency") === "true") {
            return visible.map((price) => ({
                name: price.shop.name + "." + price.shop.lang,
                data: price.priceData.map((pd) => [pd.crawlDate, getChartPrice(pd), getChartMerchantName(pd)])
            }))
        }

        return visible.map((price) => ({
            name: price.shop.name + "." + price.shop.lang,
            data: price.priceData.map((pd) => [pd.crawlDate, getChartPrice(pd), getChartMerchantName(pd)])
        }))
    }

    const { data, error, refetch } = useQuery(query, {
        displayName: "PriceHistoryQuery",
        variables: variables,
        fetchPolicy: "no-cache"
    })
    const priceHistory = data?.priceHistory ?? []

    useEffect(() => {
        if (error) console.error(error)
    }, [error])

    useEffect(() => {
        if (props.selectedProductId === undefined) {
            return
        }

        let newVariables = {
            productId: props.selectedProductId,
            shopIds:
                props.foreignShop !== null && props.foreignShop !== undefined
                    ? props.foreignShop.shopId
                    : props.shopIds,
            columnLang: props.columnLang
        }

        if (
            dateRange !== null &&
            dateRange !== undefined &&
            dateRange[0] !== null &&
            dateRange[1] !== null
        ) {
            newVariables.fromMillis = dateRange[0].getTime()
            newVariables.toMillis = dateRange[1].getTime()
        }

        setVariables(newVariables)
    }, [props.foreignShop?.shopId, props.selectedProductId, props.shopIds, dateRange, refetch])

    function prepareOptions() {
        const visible = getVisiblePriceHistory()
        let productname = props?.productname ? props?.productname : props?.foreignShop?.productName
        if (!productname) {
            productname = ""
        }
        productname = productname.replace(/^(.{90}[^\s]*).*/, "$1")

        return {
            colors: visible.map((price) => {
                if (price.shop.colorHex == null) {
                    return "#000000"
                } else {
                    return price.shop.colorHex
                }
            }),
            chart: {
                height: 350,
                type: "area",
                toolbar: {
                    show: true
                }
            },
            title: {
                text: [t("pricetable.righttab.price_history.title") + ": " + props?.productNr, productname]
            },
            dataLabels: {
                enabled: false
            },
            stroke: {
                curve: "smooth"
            },
            xaxis: {
                type: "datetime",
                tooltip: {
                    enabled: false
                }
            },
            yaxis: {
                type: "numeric"
            },
            tooltip: {
                custom: function ({ seriesIndex, dataPointIndex, w }) {
                    let data = w.globals.initialSeries[seriesIndex].data[dataPointIndex]
                    let name = w.globals.initialSeries[seriesIndex].name

                    let html = '<div class="apexcharts-tooltip-title">'
                    html +=
                        new Date(data[0]).toLocaleDateString("de-DE", {
                            year: "numeric",
                            month: "2-digit",
                            day: "numeric"
                        }) +
                        " " +
                        new Date(data[0]).toLocaleTimeString("de-DE", { hour: "numeric", minute: "numeric" })
                    html += "</div>"

                    html += "<ul class='apexchart_custom_list'>"
                    html += "<li><b>" + t("single.shop") + "</b>: " + name + "</li>"
                    if (data[1]) {
                        html += "<li><b>" + t("single.price") + "</b>: " + data[1] + "</li>"
                    }
                    if (data[2] && data[2] != "") {
                        html += "<li><b>" + t("single.dealers") + "</b>: " + data[2] + "</li>"
                    }
                    html += "</ul>"

                    return html
                }
            }
        }
    }

    const visiblePriceHistory = getVisiblePriceHistory()

    return (
        <div>
            <PPDateRangePicker value={dateRange} onChange={handleDateChange} />

            {visiblePriceHistory.length !== 0 && (
                <ReactApexChart
                    options={prepareOptions()}
                    onChange={setDateRange}
                    series={prepareSeriesDataForAreaChart()}
                    type="area"
                    width={"100%"}
                    height={250}
                />
            )}
            {visiblePriceHistory.length === 0 && (
                <p>
                    {t("pricetable.righttab.price_history.no_pricehistory_available_1")}
                    <br />
                    {t("pricetable.righttab.price_history.no_pricehistory_available_2")}
                </p>
            )}
        </div>
    )
}

export default PPPriceHistory
